/**
 * Error Message Mapper
 * Maps API error codes to user-friendly messages with helpful suggestions
 */

import type { ApiError } from '../services/api';

/**
 * User-friendly error message with optional suggestion
 */
export interface ErrorMessage {
  message: string;
  suggestion?: string;
}

/**
 * Map of error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, ErrorMessage> = {
  // Network errors
  NETWORK_ERROR: {
    message: 'Connection issue. Please check your internet connection.',
    suggestion: 'Try again in a few moments.',
  },
  TIMEOUT_ERROR: {
    message: 'Request timed out.',
    suggestion: 'Please try again.',
  },

  // Authentication errors
  UNAUTHORIZED: {
    message: 'Please sign in to continue.',
    suggestion: 'You may need to log in again.',
  },
  INVALID_CREDENTIALS: {
    message: 'Email or password is incorrect.',
    suggestion: 'Please check your credentials and try again.',
  },
  SESSION_EXPIRED: {
    message: 'Your session has expired.',
    suggestion: 'Please sign in again.',
  },

  // Validation errors
  VALIDATION_ERROR: {
    message: 'Please check your information and try again.',
    suggestion: 'Some fields may have errors.',
  },
  INVALID_EMAIL: {
    message: 'Please enter a valid email address.',
  },
  INVALID_PHONE: {
    message: 'Please enter a valid phone number.',
  },
  INVALID_ADDRESS: {
    message: 'Please enter a complete address.',
  },
  INVALID_ZIP_CODE: {
    message: 'Please enter a valid ZIP code.',
  },

  // Service area errors
  SERVICE_AREA_ERROR: {
    message: 'Service is not available in your area.',
    suggestion: 'Please contact us for more information about service coverage.',
  },
  OUT_OF_SERVICE_AREA: {
    message: 'We do not currently service this location.',
    suggestion: 'Contact us to discuss alternative options.',
  },

  // Availability errors
  NO_AVAILABILITY: {
    message: 'No available time slots for the selected date.',
    suggestion: 'Please choose a different date.',
  },
  CAPACITY_EXCEEDED: {
    message: 'Selected time slot is full.',
    suggestion: 'Please choose a different time.',
  },

  // Order errors
  ORDER_NOT_FOUND: {
    message: 'Order not found.',
    suggestion: 'Please check the order number and try again.',
  },
  ORDER_ALREADY_CANCELLED: {
    message: 'This order has already been cancelled.',
  },
  CANNOT_CANCEL_ORDER: {
    message: 'This order cannot be cancelled.',
    suggestion: 'Contact customer support for assistance.',
  },
  INVALID_ORDER_STATE: {
    message: 'This action cannot be performed on this order.',
    suggestion: 'Please contact support if you need help.',
  },

  // Payment errors
  PAYMENT_FAILED: {
    message: 'Payment could not be processed.',
    suggestion: 'Please check your payment information and try again.',
  },
  CARD_DECLINED: {
    message: 'Your card was declined.',
    suggestion: 'Please try a different payment method.',
  },
  INSUFFICIENT_FUNDS: {
    message: 'Insufficient funds.',
    suggestion: 'Please use a different payment method.',
  },
  INVALID_CARD: {
    message: 'Card information is invalid.',
    suggestion: 'Please check your card details.',
  },
  EXPIRED_CARD: {
    message: 'Your card has expired.',
    suggestion: 'Please use a different card.',
  },

  // Customer errors
  CUSTOMER_NOT_FOUND: {
    message: 'Customer account not found.',
    suggestion: 'Please check your information or create a new account.',
  },
  DUPLICATE_EMAIL: {
    message: 'An account with this email already exists.',
    suggestion: 'Try signing in or use a different email.',
  },
  DUPLICATE_PHONE: {
    message: 'An account with this phone number already exists.',
    suggestion: 'Try signing in or use a different phone number.',
  },

  // Container errors
  CONTAINER_NOT_FOUND: {
    message: 'Container not found.',
    suggestion: 'Please check the container number and try again.',
  },
  CONTAINER_ALREADY_PICKED_UP: {
    message: 'This container has already been picked up.',
  },
  CONTAINER_NOT_DELIVERED: {
    message: 'Container has not been delivered yet.',
  },

  // Pricing errors
  PRICING_UNAVAILABLE: {
    message: 'Pricing is temporarily unavailable.',
    suggestion: 'Contact us for a quote.',
  },

  // Provider errors
  PROVIDER_NOT_FOUND: {
    message: 'Service provider not found.',
  },
  PROVIDER_UNAVAILABLE: {
    message: 'Service provider is temporarily unavailable.',
    suggestion: 'Please try again later.',
  },

  // Server errors
  SERVER_ERROR: {
    message: 'Something went wrong on our end.',
    suggestion: 'Please try again in a few moments.',
  },
  INTERNAL_ERROR: {
    message: 'An unexpected error occurred.',
    suggestion: 'Please try again or contact support if the problem persists.',
  },
  SERVICE_UNAVAILABLE: {
    message: 'Service is temporarily unavailable.',
    suggestion: 'Please try again in a few minutes.',
  },

  // Rate limiting
  RATE_LIMIT_EXCEEDED: {
    message: 'Too many requests.',
    suggestion: 'Please wait a moment before trying again.',
  },

  // Default fallback
  UNKNOWN_ERROR: {
    message: 'An unexpected error occurred.',
    suggestion: 'Please try again or contact support if the problem persists.',
  },
};

/**
 * Stripe-specific error messages
 */
const STRIPE_ERROR_MESSAGES: Record<string, ErrorMessage> = {
  card_declined: {
    message: 'Your card was declined.',
    suggestion: 'Please try a different payment method.',
  },
  expired_card: {
    message: 'Your card has expired.',
    suggestion: 'Please use a different card.',
  },
  incorrect_cvc: {
    message: 'Card security code is incorrect.',
    suggestion: 'Please check the CVC and try again.',
  },
  incorrect_number: {
    message: 'Card number is incorrect.',
    suggestion: 'Please check the card number and try again.',
  },
  invalid_expiry_month: {
    message: 'Expiration month is invalid.',
    suggestion: 'Please check the expiration date.',
  },
  invalid_expiry_year: {
    message: 'Expiration year is invalid.',
    suggestion: 'Please check the expiration date.',
  },
  invalid_number: {
    message: 'Card number is invalid.',
    suggestion: 'Please check the card number and try again.',
  },
  processing_error: {
    message: 'Payment could not be processed.',
    suggestion: 'Please try again in a moment.',
  },
  insufficient_funds: {
    message: 'Insufficient funds.',
    suggestion: 'Please use a different payment method.',
  },
};

/**
 * Get user-friendly error message from error code
 *
 * @param error - Error object (ApiError or Error)
 * @param isStripeError - Whether this is a Stripe error
 * @returns User-friendly error message with optional suggestion
 */
export function getErrorMessage(
  error: unknown,
  isStripeError: boolean = false
): ErrorMessage {
  // Handle ApiError
  if (error && typeof error === 'object' && 'code' in error) {
    const apiError = error as ApiError;

    // Check Stripe errors first if applicable
    if (isStripeError && apiError.code) {
      const stripeMessage = STRIPE_ERROR_MESSAGES[apiError.code];
      if (stripeMessage) {
        return stripeMessage;
      }
    }

    // Check standard error codes
    if (apiError.code) {
      const errorMessage = ERROR_MESSAGES[apiError.code];
      if (errorMessage) {
        return errorMessage;
      }
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Check if message contains known patterns
    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR!;
    }

    if (message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR!;
    }

    if (message.includes('unauthorized') || message.includes('401')) {
      return ERROR_MESSAGES.UNAUTHORIZED!;
    }

    // Return error message as-is if it looks user-friendly
    if (error.message.length > 0 && error.message.length < 200) {
      return { message: error.message };
    }
  }

  // Default fallback
  return ERROR_MESSAGES.UNKNOWN_ERROR!;
}

/**
 * Format error message for display
 *
 * @param errorMessage - Error message object
 * @returns Formatted string for display
 */
export function formatErrorMessage(errorMessage: ErrorMessage): string {
  if (errorMessage.suggestion) {
    return `${errorMessage.message} ${errorMessage.suggestion}`;
  }
  return errorMessage.message;
}

/**
 * Check if error is retryable (transient failure)
 *
 * @param error - Error object
 * @returns Whether the error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const apiError = error as ApiError;

    // These errors are typically transient and worth retrying
    const retryableCodes = [
      'NETWORK_ERROR',
      'TIMEOUT_ERROR',
      'SERVER_ERROR',
      'SERVICE_UNAVAILABLE',
      'INTERNAL_ERROR',
    ];

    return retryableCodes.includes(apiError.code);
  }

  // Network errors are retryable
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('network') || message.includes('timeout');
  }

  return false;
}
